import { useMemo } from "react";
import { DEPOSIT_METHODS } from "../utils/registry";
import { partitionMethods } from "../utils/methods";
import type { DepositMethodId } from "../types";
import { useProfile } from "hooks/firestore/user/useProfile";
import { useIsApplePayAvailable } from "features/wallet/hooks/useApplePay";
import { useIsGooglePayAvailable } from "features/wallet/hooks/useIsGooglePayAvailable";

// static empty array reference
const DEFAULT_EXCLUSIONS: readonly DepositMethodId[] = [];

export const useAvailableDepositMethods = (
  excludedMethods: readonly DepositMethodId[] = DEFAULT_EXCLUSIONS,
) => {
  const { profile, isLoading: profileLoading } = useProfile();
  const isApplePayAvailable = useIsApplePayAvailable();
  const isGooglePayAvailable = useIsGooglePayAvailable();

  const currency = profile?.fiatCurrency;

  const methodsAndDestinations = useMemo(() => {
    const isAud = currency === "AUD";

    const availability: Record<DepositMethodId, boolean> = {
      "apple-pay": isAud && isApplePayAvailable,
      "google-pay": isAud && isGooglePayAvailable,
      "debit-card": isAud,
      payid: isAud,
      skrill: currency === "NZD",
      interac: currency === "CAD",
    };

    return partitionMethods(
      DEPOSIT_METHODS.filter(
        (method) =>
          availability[method.id] && !excludedMethods.includes(method.id),
      ),
    );
  }, [currency, isApplePayAvailable, isGooglePayAvailable, excludedMethods]);

  return {
    ...methodsAndDestinations,
    // ensures the method availability
    isSettled: !profileLoading,
  };
};
